import gsap from "gsap";
import { Rectangle } from "pixi.js";
import { Signal } from "typed-signals";
import { WinValueCounter } from "onearm";
import { Scene } from "onearm";
import { FullScreenBackgroundFill } from "onearm";
import services from "onearm";

function getAnimationDuration(spine, name) {
    const animation = spine.skeleton.data.findAnimation(name);

    return animation ? animation.duration : 0;
}

export class WinSpinePopup extends Scene {
    constructor(parameters) {
        super({ layer: "popups", ...parameters });

        const {
            winValueStyle = "WinPopupCounterStyle",
            winValueSlotName = ["win_summ_1"],
            win = 0,
            spine = "",
            sequence = [],
            isCounter = true,
            counterSfx = {
                counterLoop: "counter_loop",
                counterEnd: "counter_end",
            },
            staticValues = [],
            animationTimeScale = 1,
            counterDuration = 1,
        } = parameters;

        this.parameters = parameters;
        this.counterDuration = counterDuration;
        this.winValue = win;
        this.spine = spine;
        this.sequence = sequence;
        this.counterSfx = counterSfx;
        this.isCounter = isCounter;

        this.fill = this.addChild(
            new FullScreenBackgroundFill({
                fullScreenZone: parameters.screen.zone.fullScreen,
                color: 0x000000,
                alpha: 0.9,
            }),
        );
        this.animation = this.createObject("Spine", { name: spine, x: "50%", y: "50%" });

        this.texts = [];

        for (const slotName of winValueSlotName) {
            const text = this.createObject("Text", {
                text: isCounter  ? 0 : win,
                style: winValueStyle,
                anchor: 0.5,
            });
            this.texts.push(text);
            this.animation.addSlotObject(slotName, text);
        }

        staticValues.forEach(({style, slot, value}) => {
            const text = this.createObject("Text", {
                text: value,
                style: style,
                anchor: 0.5,
            });
            this.animation.addSlotObject(slot, text);
        });

        this.onComplete = new Signal();
        this.alpha = 0;
        this.animation.alpha = 0;
    }

    show() {
        super.show();

        this.timeline = gsap.timeline();

        this.timeline.fedeCurrentMusic(0.5, 0.25);
        this.timeline.add([
            this.timeline.to(this, { duration: 0.05, alpha: 1 }),
            this.startAnimation()
        ]);

        this.timeline.add("skipTime");
        this.timeline.to(this, { duration: 0.05, alpha: 0 }, "-=0.1");
        this.timeline.fedeCurrentMusic(1, 0.25);
        this.timeline.add(() => {
            this.animation.state.clearTrack(0);
            this.onComplete.emit();
        });

        return this.timeline;
    }

    startAnimation() {
        const timeline = gsap.timeline();

        timeline.add([
            this.startSpineAnimation(),
            this.getCountupAnimation(),
            gsap.to(this.animation, {duration: 0.2, alpha: 1 }),
        ]);


        return timeline;
    }

    getCountupAnimation() {
        const timeline = gsap.timeline();

        if (this.isCounter && this.counterDuration) {
            this.winCounter = new WinValueCounter({
                value: 0,
                duration: this.counterDuration,
                text: this.texts,
                format: services.currencyFormatter.format.bind(services.currencyFormatter),
            });
            this.winCounter.setWin(0);

            if (this.counterSfx.counterLoop) {
                timeline.playSfx(this.counterSfx.counterLoop, { loop: true });
            }
            timeline.add(this.winCounter.countTo(this.winValue));
            if (this.counterSfx.counterLoop) {
                timeline.stopSfx(this.counterSfx.counterLoop);
            }
            if (this.counterSfx.counterEnd) {
                timeline.playSfx(this.counterSfx.counterEnd);
            }
        }

        return timeline;
    }

    /**
     * @returns {gsap.core.Timeline}
     */
    startSpineAnimation() {
        const timeline = gsap.timeline();
        const timeScale = this.parameters?.animationTimeScale ?? 1;
        const lastIndex = this.sequence.length - 1;

        if (this.sequence.length === 0) {
            timeline.addLabel("lastAnimationStart", 0);
            return timeline;
        }

        for (let index = 0; index < this.sequence.length; index++) {
            const item = this.sequence[index];
            const repeatCount = Math.max(0, (item.loop || 1) - 1);
            const sfx = item.sfx || {};

            if (sfx.name) {
                timeline.playSfx(sfx.name, sfx);
            }
            if (index === lastIndex) {
                timeline.addLabel("lastAnimationStart");
            }
            timeline.to(this.animation, {
                spine: { animation: item.animation, timeScale },
                repeat: repeatCount,
            });
            if (sfx.name) {
                timeline.stopSfx(sfx.name);
            }
        }

        return timeline;
    }
}
